export const en = {
  // ─── Navbar ───
  nav: {
    home: "Home",
    about: "About Us",
    services: "Services",
    kols: "KOL Network",
    howWeWork: "How We Work",
    contact: "Contact",
    getInTouch: "Get in Touch",
  },

  // ─── Hero ───
  hero: {
    tag: "Global KOL Marketing Agency",
    headline1: "Connecting Your Brand",
    headline2: "With the World",
    sub: "From KOL partnerships to PR strategy—we help Chinese brands break through to global audiences across 103 countries.",
    cta: "Start a Campaign",
    stats: [
      { value: "1.2M+", label: "KOL Resources" },
      { value: "103", label: "Countries" },
      { value: "4B+", label: "Audience Reach" },
      { value: "50+", label: "Brand Partners" },
    ],
  },

  // ─── About ───
  about: {
    eyebrow: "About Ascend",
    headline1: "Born in China.",
    headline2: "Built for the World.",
    body: "Ascend is a global marketing agency headquartered in Shenzhen, with offices in Hong Kong. We specialize in helping Chinese brands expand internationally through influencer marketing, experiential activations, PR campaigns, and social media management. Our data-driven approach ensures every campaign delivers measurable impact.",
    advantages: [
      {
        num: "01",
        label: "Precision Targeting",
        desc: "103 countries · 27 languages · 4B+ audience reach. Our multi-dimensional audience profiling ensures every campaign reaches the right consumer.",
      },
      {
        num: "02",
        label: "Agile Execution",
        desc: "From insights to live content—our end-to-end project management dramatically shortens the time from decision to market.",
      },
      {
        num: "03",
        label: "Data-Driven",
        desc: "Real-time performance monitoring across impressions, engagement, and conversions. Transparent reporting makes every dollar accountable.",
      },
      {
        num: "04",
        label: "Long-Term Value",
        desc: "We build lasting strategic partnerships. Over 70% of our clients renew or expand their collaboration after the first project.",
      },
    ],
    platforms: "Platforms We Cover",
    statsRow: [
      { value: "1.2M+", label: "Global KOL Pool" },
      { value: "103", label: "Countries Covered" },
      { value: "4B+", label: "Total Audience Reach" },
      { value: "27", label: "Languages Supported" },
    ],
  },

  // ─── Services ───
  services: {
    eyebrow: "Our Services",
    headline1: "Full-Funnel Marketing",
    headline2: "Covering Every Growth Touchpoint",
    subtitle: "Whether you need influencer partnerships, event activations, PR coverage, or social media growth—we deliver integrated solutions tailored to your brand.",
    tabs: [
      "Influencer Marketing",
      "Experiential Marketing",
      "PR & Media Relations",
      "Social Media Management",
    ],
    items: [
      {
        title: "Influencer Marketing / Creator Partnerships",
        subtitle: "Precise Matching · Efficient Execution · Measurable ROI",
        description:
          "From creator sourcing and creative strategy to content co-creation and performance tracking, we deliver end-to-end influencer marketing across TikTok, Instagram, YouTube, Snapchat, and more—helping brands achieve authentic audience connection and sustainable conversion growth.",
        items: [
          "Global KOL database: 1.2M+ creators across 103 countries, precisely matched to your brand and target audience",
          "Creator vetting & audit: Audience analysis, engagement rate evaluation, brand affinity review, compliance screening",
          "Content strategy & co-creation: Creative scripts, filming direction, platform adaptation to maximize content impact",
          "Execution management & monitoring: Real-time tracking of views, engagement rates, and conversion data",
          "Performance analysis & optimization: Full campaign reports with continuous strategy iteration",
        ],
      },
      {
        title: "Experiential Marketing / Event Activations",
        subtitle: "Immersive Experiences · Brand Resonance · Viral Reach",
        description:
          "From brand launch events and pop-up stores to flash activations, we design participatory offline experiences that turn every attendee into an active brand advocate. Combined with creator invitations and online amplification, we close the loop between live experience and social reach.",
        items: [
          "Event concept & creative: Theme development, set design, program planning",
          "Creator invitations & on-site execution: Targeted creator outreach to amplify event buzz",
          "Online-offline integration: Real-time social amplification to extend campaign lifespan",
          "Performance tracking & debrief: Full analysis of attendance, reach, and audience feedback",
        ],
      },
      {
        title: "PR & Media Relations",
        subtitle: "Authority Coverage · Global Media Network · Precision Placement",
        description:
          "We've built a global media network spanning top-tier outlets and vertical industry publications, helping brands achieve maximum visibility and positive sentiment at key moments.",
        items: [
          "Tier-1 media placements: BBC, Forbes, TIME, CNN, Bloomberg, and more",
          "Vertical media: Technology, consumer electronics, blockchain, fashion, and niche industries",
          "Press release writing: Brand stories, news events, product launches—professional content creation",
          "Media relations management: Long-term cultivation of media relationships",
          "Sentiment monitoring & crisis PR: Real-time monitoring, rapid response, and reputation management",
        ],
      },
      {
        title: "Social Media Management",
        subtitle: "Continuous Growth · Content-Led · Community Building",
        description:
          "Beyond publishing content, we help brands build thriving communities that drive long-term fan engagement and brand loyalty. Covering TikTok, Instagram, Facebook, LinkedIn, and more—our data-driven operating strategy builds lasting brand influence.",
        items: [
          "Account diagnosis & positioning: Industry analysis, competitor tracking, differentiation strategy",
          "Content planning & creation: Trending topics, original graphics/video, visual design",
          "Community management: Fan engagement, comment moderation, user relationship building",
          "Paid amplification & optimization: Precision targeting with continuous ROI optimization",
          "Data analysis & iteration: Monthly/quarterly reports with ongoing strategy refinement",
        ],
      },
    ],
    learnMore: "Explore This Service",
    inquire: "Inquire About This Service",
    itemsLabel: "Services Include",
    cta: "Discuss Your Campaign",
  },

  // ─── KOL Showcase ───
  kol: {
    eyebrow: "KOL Network",
    headline1: "1.2 Million Creators.",
    headline2: "103 Countries.",
    sub: "From mega-influencers to niche micro-creators—our proprietary network spans every platform, category, and market. Access the right voice for your brand, anywhere in the world.",
    filters: ["All", "YouTube", "Instagram", "TikTok"],
    subscribers: "Subscribers",
    totalViews: "Total Views",
    category: "Category",
    region: "Region",
    platforms: "Active Platforms",
    partnerBrands: "Brand Collaborations",
    highlight: "Highlight",
    viewChannel: "View Channel",
    cta: "Access Full KOL Database",
    ctaSub: "1.2M+ creators across 103 countries—let us find the perfect match for your brand.",
    ctaBtn: "Request KOL Matching",
  },

  // ─── Testimonials / Case Studies ───
  testimonials: {
    eyebrow: "Case Studies",
    headline1: "Results That",
    headline2: "Speak for Themselves",
    sub: "Real campaigns. Real brands. Real impact.",
    prevPage: "Previous",
    nextPage: "Next",
    market: "Market",
    platforms: "Platforms",
    readMore: "Read more",
    collapse: "Collapse",
    reviewsTitle: "What Our Clients Say",
    caseStudies: [
      {
        brand: "Alipay",
        category: "Fintech",
        market: "South Korea · Malaysia · Hong Kong · Macau",
        headline: "Asian Games Activation: 30+ Creators Across 4 Markets, 8M+ Overseas Impressions",
        detail:
          "In August 2023, Ascend established a long-term partnership with Alipay, managing online promotions across South Korea, Malaysia, Hong Kong, and Macau. Leveraging the Hangzhou Asian Games, we engaged local creators to showcase the convenience of Alipay+ cross-border payments through authentic travel experiences.",
        metrics: [
          { value: "8M+", unit: "", label: "Overseas Impressions" },
          { value: "30+", unit: "Creators", label: "4-Market Activation" },
        ],
      },
      {
        brand: "HONGQI",
        category: "Premium EV",
        market: "Middle East · Norway",
        headline: "Ramadan Charity Challenge Ignites Middle East Buzz—$1 Donated per Share",
        detail:
          "In April–May 2023, Ascend orchestrated a Ramadan challenge campaign for HONGQI in the Middle East, inviting top KOLs from Dubai, Jordan, Saudi Arabia, and Kuwait. The 'donate $1 per share' mechanic dramatically boosted fan participation and brand favorability. In August, we followed with a Norway summer travel campaign to continue building HONGQI's premium European presence.",
        metrics: [
          { value: "Middle East + Norway", unit: "", label: "Dual-Market Strategy" },
          { value: "Charity Mechanic", unit: "", label: "High Fan Engagement" },
        ],
      },
      {
        brand: "TEMU",
        category: "Cross-Border E-Commerce",
        market: "Global Multi-Market",
        headline: "KOL Matrix Across 12 Countries Rapidly Builds Global Brand Awareness",
        detail:
          "Ascend designed a bespoke multi-market KOL program for TEMU, aggregating 500+ local creators across 12 countries to precisely reach target consumers and accelerate brand recognition globally.",
        metrics: [
          { value: "12", unit: "Markets", label: "Global Coverage" },
          { value: "500+", unit: "Creators", label: "Campaign Scale" },
        ],
      },
      {
        brand: "Ulike",
        category: "Beauty & Personal Care",
        market: "United States",
        headline: "Multi-Platform KOL Seeding Breaks Through the US Market for Hair Removal Brand",
        detail:
          "Ascend selected premium US-based KOLs for Ulike across TikTok, Instagram, and YouTube. Authentic usage content precisely communicated the brand's core value, significantly boosting awareness and purchase conversion among target consumers.",
        metrics: [
          { value: "3", unit: "Platforms", label: "Full-Channel Coverage" },
          { value: "US", unit: "Market", label: "Precision Breakthrough" },
        ],
      },
      {
        brand: "Hisense",
        category: "Consumer Electronics",
        market: "USA · Spain · South Africa",
        headline: "Synchronized 3-Country Campaign—300K+ Organic Impressions in 15 Days",
        detail:
          "In November–December 2023, Ascend coordinated brand campaigns for Hisense across the US, Spain, and South Africa simultaneously. Powered by local creators and precision content strategy, the campaign generated 300K+ organic impressions in just 15 days.",
        metrics: [
          { value: "300K+", unit: "", label: "Organic Impressions in 15 Days" },
          { value: "3 Countries", unit: "Simultaneous", label: "Multi-Market Activation" },
        ],
      },
      {
        brand: "Macao Tourism Board",
        category: "Tourism Promotion",
        market: "International Multi-Market",
        headline: "First-Person Creator Content Elevates Macao's Global Tourism Profile",
        detail:
          "Ascend maintains a long-term strategic partnership with the Macao Tourism Board, inviting creators from diverse countries to explore Macao firsthand—capturing UNESCO heritage sites, entertainment, and local cuisine to reinforce Macao's image as a world-class destination.",
        metrics: [
          { value: "Multi-National", unit: "Creators", label: "Matrix Coverage" },
          { value: "Long-Term", unit: "Partnership", label: "Strategic Collaboration" },
        ],
      },
      {
        brand: "SENSER",
        category: "Luxury Retail",
        market: "Hong Kong · Macau",
        headline: "Precision Penetration of HK & Macau Premium Segments—Luxury Platform Localization",
        detail:
          "Ascend partnered with SENSER on influencer marketing in Hong Kong and Macau, leveraging premium local KOLs to reach high-net-worth consumers and support SENSER's 2,500+ brand boutiques across 60+ countries.",
        metrics: [
          { value: "2500+", unit: "", label: "Brand Boutiques" },
          { value: "60+", unit: "Countries", label: "Global Network" },
        ],
      },
      {
        brand: "LYNK & CO",
        category: "New Energy Vehicle",
        market: "Middle East",
        headline: "20 Middle East KOLs in a TikTok Challenge—20M Views in 5 Days",
        detail:
          "In November 2023, Ascend launched a TikTok challenge for LYNK & CO in the Middle East, engaging 20 top local creators. The campaign surpassed 20M views in 5 days—an unprecedented burst of brand momentum.",
        metrics: [
          { value: "20M", unit: "", label: "Views in 5 Days" },
          { value: "20", unit: "KOLs", label: "Middle East Top Tier" },
        ],
      },
      {
        brand: "Jagat",
        category: "Metaverse Social",
        market: "Japan",
        headline: "TikTok Creator Matrix Breaks Into Japanese Market—2.5M Impressions",
        detail:
          "In May 2023, Ascend partnered with Jagat's virtual social platform to launch its Japan market campaign, matching local TikTok creators to deliver immersive interaction content and rapidly capture Japan's youth audience—achieving 2.5M impressions.",
        metrics: [
          { value: "2.5M", unit: "", label: "Impressions" },
          { value: "TikTok", unit: "", label: "Core Platform" },
        ],
      },
      {
        brand: "imo",
        category: "Social Communication",
        market: "Global",
        headline: "50 Creators on Dual Platforms Drive 5.3M Hashtag Views",
        detail:
          "In November 2023, Ascend coordinated 50 creators for imo's free global video call platform, publishing content simultaneously on TikTok and Instagram. Brand hashtag views reached 5.3M, effectively expanding imo's user awareness in target markets.",
        metrics: [
          { value: "5.3M", unit: "", label: "Hashtag Views" },
          { value: "50", unit: "Creators", label: "Dual Platform" },
        ],
      },
      {
        brand: "POIZON",
        category: "Streetwear E-Commerce",
        market: "UK · USA · South Korea · Indonesia",
        headline: "Long-Term KOL Matrix Across 4 Countries Sustains Streetwear Platform Growth",
        detail:
          "Ascend manages POIZON's ongoing monthly KOL programs across the UK, USA, South Korea, and Indonesia—covering YouTube, TikTok, and Instagram—delivering consistent, high-quality streetwear content to build stable brand momentum among global youth consumers.",
        metrics: [
          { value: "4 Markets", unit: "", label: "Long-Term Partnership" },
          { value: "Monthly", unit: "Ongoing", label: "Stable Investment" },
        ],
      },
      {
        brand: "Chunlai",
        category: "F&B Chain",
        market: "Southeast Asia",
        headline: "Child Star Endorsement + Offline Activation Drives Youth Brand Breakthrough",
        detail:
          "Ascend arranged a short-term endorsement collaboration between Chunlai Tea and Indonesian child star Tatan, syncing online social promotion with offline store activation—creating customized in-store materials for an immersive consumer experience that effectively elevated brand heat.",
        metrics: [
          { value: "Online + Offline", unit: "", label: "Full-Channel Activation" },
          { value: "Tatan", unit: "Endorsement", label: "Child Star Effect" },
        ],
      },
      {
        brand: "Dahz Ice Cacao",
        category: "Tea & Beverage",
        market: "Southeast Asia",
        headline: "Holiday IP × Child Star Momentum—Christmas Campaign Breaks Into Youth Segment",
        detail:
          "Over the Christmas and New Year period, Ascend arranged a co-branding collaboration between Dahz Ice Cacao and child star Tatan, activating online and offline channels simultaneously. Holiday-themed in-store materials enhanced the consumer experience, driving strong seasonal brand heat across 160+ locations.",
        metrics: [
          { value: "160+", unit: "Locations", label: "Store Activation" },
          { value: "Holiday Hit", unit: "", label: "Social Buzz" },
        ],
      },
      {
        brand: "ELFBAR",
        category: "E-Vapor",
        market: "USA · Europe · Middle East · Southeast Asia",
        headline: "Omni-Channel KOL Matrix Drives Deep Coverage Across 8+ Countries",
        detail:
          "Since August 2022, Ascend has provided global omni-channel promotion for ELFBAR, deeply penetrating the US, UK, Germany, France, Canada, Middle East, Southeast Asia, and Greece—helping the brand build international recognition and rapid user growth.",
        metrics: [
          { value: "8+", unit: "Countries", label: "Market Coverage" },
          { value: "KOL + PR", unit: "", label: "Dual Track Strategy" },
        ],
      },
      {
        brand: "Geekvape",
        category: "E-Vapor",
        market: "Key International Markets",
        headline: "11-Creator UGC Challenge Drives 2.43M Total Views",
        detail:
          "For Geekvape's 'Hunt Geekvape' campaign, Ascend selected 11 overseas creators to showcase 'lightning moments' in 30–60 second videos, encouraging followers to submit UGC—significantly boosting brand awareness and fan engagement.",
        metrics: [
          { value: "2.43M", unit: "", label: "Total Views" },
          { value: "11", unit: "Creators", label: "UGC Activation" },
        ],
      },
      {
        brand: "Vaporesso",
        category: "E-Vapor",
        market: "Global",
        headline: "Deep Brand Co-Building Helps Vaporesso Establish a Global KOL Ecosystem",
        detail:
          "Ascend entered a long-term strategic partnership with Vaporesso—from creator selection and content co-creation to full-platform distribution—helping the brand build consistent, sustained presence in overseas markets.",
        metrics: [
          { value: "Long-Term", unit: "Partnership", label: "Strategic Alliance" },
          { value: "Global", unit: "Matrix", label: "KOL Ecosystem" },
        ],
      },
      {
        brand: "DOJO",
        category: "E-Vapor",
        market: "United Kingdom",
        headline: "TikTok + Instagram Dual-Platform Launch Drives 3.76M Impressions",
        detail:
          "For the DOJO BLAST2000 GO launch, Ascend integrated 11 UK-based creators using a TikTok-first (82%) + Instagram (18%) channel mix to precisely communicate key selling points to a young, engaged audience.",
        metrics: [
          { value: "3.76M", unit: "", label: "Total Impressions" },
          { value: "11", unit: "Creators", label: "UK Local" },
        ],
      },
      {
        brand: "Lost Vape",
        category: "Premium E-Vapor",
        market: "Poland",
        headline: "New Product Teaser × Local KOLs—Lost Vape Breaks Into the Polish Market",
        detail:
          "In April 2023, Ascend partnered with Lost Vape to support their new product launch in Poland. Carefully selected creators aligned with the brand's aesthetic delivered authentic reviews and lifestyle content to precisely penetrate Poland's young consumer base.",
        metrics: [
          { value: "Poland", unit: "Market", label: "Precision Placement" },
          { value: "New Product", unit: "Teaser", label: "Brand Breakthrough" },
        ],
      },
    ],
    reviews: [
      {
        quote:
          "Ascend's deep understanding of international markets was immediately evident. They're not just an execution team—they're true strategic partners who genuinely understand brands. Every stage, from planning to the final debrief, was executed with exceptional professionalism.",
        author: "Sarah Mitchell",
        title: "Global Brand Director",
        company: "Leading Cross-Border E-Commerce Brand",
        market: "USA · Europe",
      },
      {
        quote:
          "The KOL vetting process was rigorous and completely transparent. Every creator was a precise fit for our brand. After partnering with Ascend, our Middle East presence exploded—20M views in 5 days. It far exceeded our expectations.",
        author: "James Whitfield",
        title: "Head of Marketing",
        company: "Premium New Energy Vehicle Brand",
        market: "Middle East · Saudi Arabia",
      },
      {
        quote:
          "Ascend helped us scale rapidly across Southeast Asia. Their deep knowledge of local creator ecosystems and content localization capabilities gave us a distinct competitive advantage, with ROI far surpassing industry benchmarks.",
        author: "Lucas Bennett",
        title: "Head of Growth",
        company: "Global Tech & Social Platform",
        market: "Southeast Asia · Japan",
      },
    ],
  },

  // ─── How We Work ───
  howWeWork: {
    eyebrow: "How We Work",
    headline1: "Seven Steps to",
    headline2: "Campaign Excellence",
    sub: "A rigorous, transparent, and results-focused process—so you always know what's happening, and why.",
    body: "We've refined a battle-tested process that keeps campaigns on time, on-brand, and on-target—across every market and platform.",
    tagline: "Seven steps. Zero surprises.",
    steps: [
      {
        num: "01",
        enLabel: "Discovery",
        title: "Discovery & Strategy",
        summary: "We start by listening—clarifying KPIs, budgets, and success metrics before any creative decisions.",
        detail:
          "We start by listening. Through in-depth conversations, we gain a thorough understanding of your brand DNA, target market, and growth objectives. Before any creative decisions are made, we establish clear KPIs, budget frameworks, and success criteria.",
      },
      {
        num: "02",
        enLabel: "KOL Selection",
        title: "Data-Driven KOL Selection",
        summary: "We screen hundreds of data dimensions—focusing on audience profiles, engagement quality, and brand fit.",
        detail:
          "Using our proprietary analytics platform, we screen creators across hundreds of data dimensions to find the ideal match. Beyond follower count, we analyze audience profiles, engagement quality, content authenticity, and brand alignment—every match is meticulously designed.",
      },
      {
        num: "03",
        enLabel: "Negotiation",
        title: "Creator Outreach & Negotiation",
        summary: "We manage all negotiations on your behalf, ensuring transparent terms that fully protect your interests.",
        detail:
          "We handle all creator communications, relationship-building, and contract negotiations—ensuring clarity on deliverables, usage rights, timelines, and fees to fully protect your interests.",
      },
      {
        num: "04",
        enLabel: "Co-Creation",
        title: "Creative Co-Creation",
        summary: "Our strategy and creative team works closely with creators. You retain final approval before anything goes live.",
        detail:
          "Our strategy and creative team collaborates closely with creators to develop content that fits the platform ecosystem while reflecting your brand identity and local cultural context. Before publication, you hold final approval—balancing brand consistency with creative freedom.",
      },
      {
        num: "05",
        enLabel: "Publishing",
        title: "Coordinated Publishing",
        summary: "We orchestrate publishing timing across platforms, coordinating compliance and multi-channel amplification.",
        detail:
          "We orchestrate publishing schedules across Instagram, TikTok, YouTube, and beyond—coordinating timing, compliance requirements, cross-channel amplification, and community engagement to maximize reach and authentic interaction.",
      },
      {
        num: "06",
        enLabel: "Optimization",
        title: "Real-Time Tracking & Optimization",
        summary: "Live dashboards track all key metrics; transparent weekly and monthly reports keep you fully informed.",
        detail:
          "Through real-time dashboards, we continuously track impressions, engagement, conversions, and ROI—delivering transparent weekly and monthly reports with mid-campaign optimization based on performance data.",
      },
      {
        num: "07",
        enLabel: "Debrief",
        title: "Debrief & Content Asset Reuse",
        summary: "Comprehensive post-campaign reports, plus repurposing top-performing content as paid media assets.",
        detail:
          "After each campaign, we deliver a comprehensive debrief including KPI performance, strategic insights, and recommendations for future optimization. Top-performing content is repurposed as paid media assets to extend ROI.",
      },
    ],
    cta: "Start Your Campaign",
    ctaTitle: "Ready to Start a Partnership?",
    ctaSub: "Contact us for a bespoke marketing strategy.",
    ctaBtn: "Get in Touch",
  },

  // ─── Contact ───
  contact: {
    eyebrow: "Contact Us",
    headline1: "Let's Build Something",
    headline2: "Together",
    sub: "Ready to take your brand global? Tell us about your campaign and we'll get back to you within 24 hours.",
    sent: "Message Sent!",
    sentBody: "Thank you for reaching out. Our team will get back to you within 24 hours.",
    emailSubject: "Campaign Inquiry from {name}",
    fieldName: "Full Name",
    fieldCompany: "Company",
    fieldEmail: "Email Address",
    fieldMessage: "Message",
    fieldMessagePlaceholder: "Tell us about your campaign goals, target markets, and budget range...",
    formTitle: "Start a Conversation",
    formSub: "We'll respond within 24 hours",
    send: "Send Message",
    items: [
      { label: "Shenzhen HQ", value: "11/F, Pearl River International Center\nLonggang District, Shenzhen" },
      { label: "Hong Kong Office", value: "Room 1022A, 87-105 Nathan Road\nTsim Sha Tsui, Kowloon" },
      { label: "Email", value: "sylvia@chinaascend.net\njulien@chinaascend.net" },
      { label: "Phone", value: "+86 153 2382 3591" },
      { label: "Telegram / WhatsApp", value: "+86 15323823591" },
      { label: "WeChat", value: "1647226901" },
      { label: "Instagram", value: "@ascend_marketingagency" },
    ],
    form: {
      name: "Full Name",
      company: "Company",
      email: "Email Address",
      message: "Tell us about your campaign",
      send: "Send Message",
      success: "Message sent! We'll be in touch within 24 hours.",
    },
  },

  // ─── Footer ───
  footer: {
    tagline: "Global Influencer Marketing & Brand Growth Agency",
    navCols: [
      {
        title: "Services",
        links: ["Influencer Marketing", "Experiential Marketing", "PR & Media Relations", "Social Media Management"],
      },
      {
        title: "Company",
        links: ["Our Story", "Core Business", "Our Advantages", "Partnership Philosophy"],
      },
      {
        title: "Connect",
        links: ["Get in Touch", "Shenzhen HQ", "Hong Kong Office", "Send Email"],
      },
    ],
    legal: ["Privacy Policy", "Terms of Service"],
  },

  // ─── KOL Showcase (KOLShowcase component) ───
  kols: {
    eyebrow: "KOL Resources · Global Creator Network",
    headline1: "Premium KOL Resources",
    headline2: "Covering Every Global Category",
    subscribers: "Subscribers",
    views: "Total Views",
    viewChannel: "View Channel",
    brandPartners: "Brand Collaborations",
    kolCtaText: "Interested in collaborating with this KOL? Contact us for a tailored proposal.",
    getProposal: "Get Collaboration Proposal",
    resourceNote: "Partial showcase · We maintain 1.2M+ global KOL resources",
    getFullList: "Access Full KOL Database",
  },
};

export type Translations = typeof en;